(ns hai.tasks.ui
  "Completion stamp, the quick-add composer shell, and the task form
   (title/description/deadline/links/assignee)."
  (:require
   [clojure.string :as str]
   [hai.notes.links :as links]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as data]
   [hai.tasks.view :as view]
   [hai.ui.components.date-picker :as date-picker]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.components.form.core :as form]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [hai.web-app.platform :as platform]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private desc-icon (icons/mui-icon :outlined :notes))
(def ^:private deadline-icon (icons/mui-icon :outlined :event))
(def ^:private link-icon (icons/mui-icon :outlined :link))
(def ^:private close-icon (icons/mui-icon :outlined :close))
(def ^:private person-icon (icons/mui-icon :outlined :person))

(defn- hash01
  "Deterministic value in [0, 1) from any seed."
  [seed]
  (/ (mod (hash seed) 100000) 100000))

(defn stamp-for
  "Stamp visuals (rotation, offset, per-side border thickness) derived from a
   task title — same title always renders the same impression. Border range
   1.5-3.8 px so adjacent sides read as visibly uneven hand-stamp pressure
   without the heavy end looking blocky."
  [title]
  (let [r  (fn [salt] (hash01 [title salt]))
        bw (fn [salt] (+ 1.5 (* (r salt) 2.3)))]
    {:rot (- (* (r :rot) 24) 12)
     :dx  (- (* (r :dx) 14) 7)
     :dy  (- (* (r :dy) 12) 6)
     :bt  (bw :bt)
     :br  (bw :br)
     :bb  (bw :bb)
     :bl  (bw :bl)}))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

(o/defstyled completion-stamp :div
  {:border-style "solid"
   :border-color t/--property-15
   :color t/--property-15
   :border-radius "7px"
   :display "flex" :align-items "center" :justify-content "center"
   :pointer-events "none"
   :user-select "none"
   :-webkit-user-select "none"
   :opacity "0.88"
   :mix-blend-mode "multiply"
   :background-image "radial-gradient(circle at 28% 32%, rgba(255,255,255,0.55) 0%, transparent 32%), radial-gradient(circle at 72% 75%, rgba(255,255,255,0.35) 0%, transparent 28%)"}
  [:.check {:font-weight "700" :line-height "1"}]
  ([{:keys [task-title centered?]}]
   (let [{:keys [rot dx dy bt br bb bl]} (stamp-for task-title)
         ;; The dx/dy jitter is the off-center "stamp pressure" inside a
         ;; stampbox; stamps laid out in a row keep only the rotation.
         transform (if centered?
                     (str "translate(calc(-50% + " dx "px), calc(-50% + " dy "px))"
                          " rotate(" rot "deg)")
                     (str "rotate(" rot "deg)"))]
     [:<>
      {:style {:transform transform
               :border-width (str bt "px " br "px " bb "px " bl "px")}}
      [:span.check "✔"]])))

;; A dashed box that stamps itself with [[completion-stamp]] when the task is
;; done — click (or Enter/Space) toggles completion. Shared by the mini-app
;; cards and the tasks table row.
(o/defstyled stampbox :button
  {:width "34px" :height "34px"
   :border-width "2px" :border-style "dashed" :border-color t/--property-3
   :border-radius "8px"
   :background "rgba(255,255,255,0.5)"
   :position "relative"
   :flex "0 0 auto"
   :cursor "pointer" :outline "none"}
  [:&:focus-visible
   {:border-color t/--card-forest
    :box-shadow "0 0 0 2px #F7F6EF, 0 0 0 3.5px #2D3E2A"}]
  [:&.stamped
   {:border-color "rgba(45,62,42,0.18)"
    :background "rgba(255,255,255,0.3)"}]
  ;; Hover feedback only where hover exists — on touch the browser pins :hover
  ;; to the last-tapped element, which would leave it stuck on.
  [:at-media {:hover "hover"}
   [:&:hover {:border-style "solid" :border-color t/--card-forest}]]
  [:&:disabled
   {:cursor "not-allowed"
    :opacity "0.5"}]
  [completion-stamp
   {:position "absolute"
    :left "50%" :top "50%"
    :width "32px" :height "32px"}
   [:.check {:font-size "22px"}]]
  ([{:keys [done? task-title stamp-key disabled? title on-toggle]}]
   [:<>
    {:class      (when done? "stamped")
     :type       "button"
     :disabled   (boolean disabled?)
     :title      (or title
                     (when disabled? "You don't have permission to complete this task")
                     (if done? "Mark incomplete" "Mark complete"))
     :aria-label (if done? "remove stamp" "stamp complete")
     :on-click   on-toggle}
    (when done?
      ^{:key (or stamp-key task-title)}
      [completion-stamp {:task-title task-title :centered? true}])]))

(o/defstyled quick-add :div
  {:margin "10px 14px 4px"
   :padding "8px 12px"
   :border-radius "10px"
   ;; Transparent until expanded so the lone title field doesn't sit inside a
   ;; second, redundant border.
   :border "1.5px solid transparent"
   :background "transparent"}
  [#{:&.open :&.editing}
   {:border-color "#C2CDB1"
    :background "rgba(184,196,166,0.18)"}]
  [:&.editing
   {:margin "6px 14px"}]
  ;; Host (e.g. the mobile card) owns the spacing.
  [:&.flush {:margin "0"}]
  [:.quick-add-row
   {:display "flex" :flex-direction "row"
    :align-items "center" :gap "10px"}]
  [:.quick-add-input-wrap
   {:flex "1 1 0"
    :min-width "0"}]
  [:.quick-add-title-input
   {:width "100%"
    :padding "9px 12px"
    :background "#FFFFFF"
    :border "1px solid #D9E0CE" :border-radius "8px"
    :outline "none"
    :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
    :font-family "inherit"
    :font-size "15px" :font-weight "500" :line-height "1.4"
    :color "#2D3E2A"
    :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
   [:&:focus {:border-color t/--card-sage
              :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]
   [:&::placeholder {:color "#6B7560"}]
   [:&.missing::placeholder
    {:font-style "italic"
     :color "rgba(107,117,96,0.55)"}]]
  [:.quick-add-desc-wrap
   {:margin-top "10px"
    :display "flex" :flex-direction "column" :gap "8px"}]
  [:.quick-add-desc
   {:width "100%"
    :padding "9px 32px 9px 12px"
    :background "#FFFFFF"
    :border "1px solid #D9E0CE" :border-radius "8px"
    :outline "none" :resize "none"
    :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
    :font-family "inherit"
    :font-size "13px" :line-height "1.5"
    :color "#2D3E2A"
    ;; Grow with content instead of scrolling; min height keeps the empty
    ;; field a comfortable text-area size.
    :field-sizing "content"
    :min-height "5em"
    :overflow "hidden"
    :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
   [:&:focus {:border-color t/--card-sage
              :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]
   [:&::placeholder {:color "#6B7560"}]]
  [:.quick-add-pills
   {:display "flex" :flex-wrap "wrap" :gap "8px"
    :margin-top "10px"}
   ;; When sections are open above, fence the "Add …" triggers off from them.
   [:&.separated
    {:margin-top "12px"
     :padding-top "12px"
     :border-top "1px solid #D9E0CE"}]]
  [:.quick-add-pill
   {:display "inline-flex" :align-items "center" :gap "4px"
    :align-self "flex-start"
    :padding "5px 12px"
    :border-width "1px" :border-style "dashed" :border-color t/--card-sage
    :border-radius "999px"
    :background "transparent"
    :font-family "inherit"
    :font-size "12px" :font-weight "500"
    :color "#5B6F4E"
    :cursor "pointer"
    :transition "color 0.15s ease, border-color 0.15s ease, background 0.15s ease"}
   [:&:hover {:color t/--card-forest :border-color t/--card-forest
              :background t/--card-forest-06}]
   [:&.active {:background t/--property-3 :border-style "solid"
               :border-color t/--card-sage-light :color t/--card-forest}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.quick-add-deadline-wrap
   {:margin-top "10px"
    :display "flex" :flex-direction "column" :gap "10px"}]
  [:.deadline-options
   {:display "flex" :flex-wrap "wrap" :align-items "center" :gap "8px"}]
  [:.deadline-done
   {:align-self "flex-start"
    :padding "6px 14px"
    :border "none" :border-radius "999px"
    :background t/--card-forest :color "white"
    :font-family "inherit"
    :font-size "12px" :font-weight "600"
    :cursor "pointer"
    :transition "opacity 0.15s ease"}
   [:&:hover {:opacity "0.9"}]]
  [:.quick-add-links-wrap
   {:margin-top "10px"
    :display "flex" :flex-direction "column" :gap "10px"}]
  [:.quick-add-assignee-wrap
   {:margin-top "10px"
    :display "flex" :flex-direction "column" :align-items "flex-start"
    :gap "10px"}]
  ;; Same filled-chip look as `deadline-chip`, minus the ✕ — there's no
  ;; unassign mutation, a task is always assigned to someone.
  [:.quick-add-assignee-chip
   {:display "inline-flex" :align-items "center" :gap "6px"
    :padding "5px 12px"
    :border-width "1px" :border-style "solid" :border-color t/--card-sage-light
    :border-radius "999px"
    :background t/--property-3
    :font-family "inherit"
    :font-size "12px" :font-weight "500"
    :color t/--card-forest
    :line-height "1"}
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.quick-add-footer
   {:display "flex" :align-items "center" :justify-content "flex-end"
    :gap "8px"
    :margin-top "16px"}]
  [:.quick-add-hint
   {:font-size "11px" :font-weight "500"
    :color "#6B7560"
    :letter-spacing "0.02em"
    :padding "3px 7px"
    :border-radius "5px"
    :background "rgba(45,62,42,0.06)"
    :margin-right "auto"
    :pointer-events "none"}]
  [:.quick-add-cancel
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :font-family "inherit"
    :font-size "12px" :color t/--link-muted
    :cursor "pointer"
    :border-radius "6px"
    :transition "color 0.15s ease, background 0.15s ease"}
   [:&:hover {:color t/--card-forest :background t/--card-forest-06}]]
  [:.quick-add-save
   {:padding "6px 12px"
    :border "none" :border-radius "6px"
    :background t/--card-forest :color "white"
    :font-family "inherit"
    :font-size "12px" :font-weight "600"
    :cursor "pointer"
    :transition "opacity 0.15s ease"}
   [:&:disabled {:opacity "0.35" :cursor "not-allowed"}]])

;; Labeled header for each expanded section: "[icon] LABEL ────────".
(o/defstyled section-divider :div
  {:display "flex" :align-items "center" :gap "6px"}
  [:.section-divider-icon
   {:display "inline-flex" :align-items "center"
    :color t/--card-forest-70}
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.section-divider-label
   {:font-size "10px" :font-weight "700" :letter-spacing "0.06em"
    :text-transform "uppercase"
    :color t/--card-forest-70
    :white-space "nowrap"}]
  [:.section-divider-rule
   {:flex "1"
    :border-top-width "1px" :border-top-style "dashed"
    :border-top-color t/--card-sage-light}]
  ([{:keys [icon label]}]
   [:<>
    [:span.section-divider-icon [icon]]
    [:span.section-divider-label label]
    [:span.section-divider-rule]]))

;; Filled chip with an inline remove segment divided off by a left border, like
;; the tag chip — used for the selected date and the time.
(o/defstyled deadline-chip :span
  {:display "inline-flex" :align-items "stretch"
   :align-self "flex-start"
   :border-radius "999px"
   :overflow "hidden"
   :border-width "1px" :border-style "solid" :border-color t/--card-sage-light
   :background t/--property-3
   :color t/--card-forest
   :line-height "1"}
  [:.deadline-chip-label
   {:display "inline-flex" :align-items "center" :gap "5px"
    :padding "5px 12px"
    :border "none" :background "transparent"
    :font-family "inherit" :font-size "12px" :font-weight "500"
    :color "inherit" :cursor "pointer"}
   [:svg {:width "14px" :height "14px"}]]
  [:.deadline-chip-remove
   {:display "grid" :place-items "center"
    :width "30px"
    :border "none"
    :border-left-width "1px" :border-left-style "solid"
    :border-left-color t/--card-forest-14
    :background "transparent"
    :color t/--card-forest
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--card-sage}]
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]]
  ([{:keys [icon label on-click on-remove]}]
   [:<>
    [:button.deadline-chip-label {:type "button" :on-click on-click}
     (when icon [icon]) label]
    [:button.deadline-chip-remove
     {:type "button" :aria-label "Remove" :title "Remove"
      :on-click (fn [e] (.stopPropagation e) (on-remove))}
     [close-icon]]]))

;; ---------------------------------------------------------------------------
;; View functions
;; ---------------------------------------------------------------------------

(defn- submit-hint
  [{:keys [multiline? action]}]
  (if multiline?
    (str (if platform/mac? "⌘" "Ctrl") " ↵ to " action)
    (str "↵ to " action)))

(defn- read-fields
  "Current value and setter for each task form field, plus the raw deadline
   field the date picker needs."
  [fields]
  (let [{title-field :task/title
         desc-field :task/description
         urgency-field :task/urgency
         deadline-field :task/deadline
         links-field :activity/urls} fields]
    {:title-val (:form.field/current-value title-field)
     :desc-val (:form.field/current-value desc-field)
     :urgency-val (:form.field/current-value urgency-field)
     :deadline-val (:form.field/current-value deadline-field)
     :link-urls (vec (:form.field/current-value links-field))
     :deadline-field deadline-field
     :set-title! (:form.field/set-value! title-field)
     :set-desc! (:form.field/set-value! desc-field)
     :set-urgency! (:form.field/set-value! urgency-field)
     :set-deadline! (:form.field/set-value! deadline-field)
     :set-links! (:form.field/set-value! links-field)}))

(defn- initial-open-sections
  "Sections that already have content when the form mounts, in display order."
  [fields]
  (let [{:keys [desc-val deadline-val link-urls]} (read-fields fields)]
    (cond-> []
      (seq desc-val) (conj :description)
      (some? (:date deadline-val)) (conj :deadline)
      (seq link-urls) (conj :links))))

(defn- title-input
  [{:keys [value placeholder missing? auto-focus? on-change on-submit on-cancel]}]
  [:div.quick-add-row
   [:div.quick-add-input-wrap
    [:input.quick-add-title-input
     {:type "text"
      :default-value (or value "")
      :auto-focus auto-focus?
      :placeholder (if missing? "Title is required…" placeholder)
      :class (when missing? "missing")
      :on-change #(on-change (.. % -target -value))
      :on-key-down (fn [e]
                     (case (.-key e)
                       "Enter" (do (.preventDefault e) (on-submit))
                       "Escape" (do (.preventDefault e) (on-cancel))
                       nil))}]]])

(defn- description-section
  [{:keys [value input-ref on-change on-submit on-cancel on-close]}]
  [:div.quick-add-desc-wrap
   [section-divider {:icon desc-icon :label "Description"}]
   [:textarea.quick-add-desc
    {:default-value (or value "")
     :rows 2
     :ref input-ref
     :placeholder "Description"
     :on-change #(on-change (.. % -target -value))
     ;; Empty + clicking away closes the section for them.
     :on-blur (fn [e]
                (when (str/blank? (.. e -target -value))
                  (on-close)))
     :on-key-down (fn [e]
                    (cond
                      (and (= "Enter" (.-key e))
                           (or (.-metaKey e) (.-ctrlKey e)))
                      (do (.preventDefault e) (on-submit))
                      (= "Escape" (.-key e))
                      (do (.preventDefault e) (on-cancel))))}]])

(def ^:private default-deadline-time-minutes (* 9 60))

(defn- date-only-label
  "Absolute label for the value's date, ignoring any time."
  [value]
  (some-> (datetime/value->moment (dissoc value :time))
          datetime/date-label))

(defn- time-label
  "'9:00 AM' from minutes since midnight."
  [minutes]
  (let [date (js/Date.)]
    (.setHours date (quot minutes 60) (mod minutes 60) 0 0)
    (.toLocaleTimeString date "en-US" #js {:hour "numeric" :minute "2-digit"})))

(defn- deadline-section
  "The full picker behind the Choose-date pill. The picked date shows as a chip
   whose ✕ clears the deadline and closes the section; Add time (its own row)
   reveals the time picker. The open picker has a Done button to collapse it —
   Done with no date picked closes the whole section. `pending-calendar?`
   (consumed at mount) starts the calendar open when the Choose-date pill is
   what opened the section."
  [{:keys [field on-remove pending-calendar?]}]
  (r/with-let [!calendar-open? (r/atom @pending-calendar?)
               _ (reset! pending-calendar? false)
               !time-open? (r/atom false)]
    (let [value (:form.field/current-value field)
          set-value! (:form.field/set-value! field)
          has-date? (some? (:date value))
          has-time? (some? (:time value))
          calendar-open? @!calendar-open?
          time-open? @!time-open?
          done! (fn []
                  (if has-date?
                    (do (reset! !calendar-open? false)
                        (reset! !time-open? false))
                    (on-remove)))]
      [:div.quick-add-deadline-wrap
       [section-divider {:icon deadline-icon :label "Deadline"}]
       (when has-date?
         [:div.deadline-options
          [deadline-chip {:icon deadline-icon
                          :label (date-only-label value)
                          :on-click #(swap! !calendar-open? not)
                          :on-remove on-remove}]])
       (when has-date?
         (if has-time?
           [deadline-chip
            {:label (time-label (:time value))
             :on-click #(swap! !time-open? not)
             :on-remove (fn []
                          (set-value! (dissoc value :time))
                          (reset! !time-open? false))}]
           [:button.quick-add-pill
            {:type "button"
             :on-click (fn []
                         (set-value! (assoc value :time default-deadline-time-minutes))
                         (reset! !time-open? true))}
            [datetime/clock-icon] "Add time"]))
       (when (or calendar-open? time-open?)
         [:<>
          [date-picker/date-picker
           {:value value
            :on-change set-value!
            :show-mode-toggle? false
            :show-quick-chips? false
            :show-calendar? calendar-open?
            :show-time? time-open?}]
          [:button.deadline-done
           {:type "button" :on-click done!}
           "Done"]])])))

(defn- links-section-view
  "The added links (each with its own ✕) stay visible; the adder toggles with an
   inline Add-links pill, so collapsing it keeps the links. Mirrors the shared
   note-extras links section."
  [{:keys [urls adder-open? on-add on-remove-link on-open-adder on-cancel-adder]}]
  [:div.quick-add-links-wrap
   (when (or (seq urls) adder-open?)
     [section-divider {:icon link-icon :label "Links"}])
   (when (seq urls)
     [links/link-list {:links urls :on-remove on-remove-link}])
   (if adder-open?
     [links/link-adder
      {:always-open? true :on-add on-add :on-cancel on-cancel-adder}]
     [:button.quick-add-pill
      {:type "button" :on-click on-open-adder}
      [link-icon] "Add links"])])

(defn- assignee-section
  "Who the task is assigned to, as a chip that opens the same contact picker the
   task rows use. Picking only stages the contact — `on-save` is what applies
   it — so Cancel leaves the current assignee alone. `assignee` is either the
   task's `:task/assigned-to` user or an already-staged contact."
  [{:keys [assignee on-pick]}]
  [:div.quick-add-assignee-wrap
   [section-divider {:icon person-icon :label "Assignee"}]
   [assignee-picker/component
    {:editable?     true
     :on-pick       on-pick
     :trigger-class "pill"
     :label         "Change assignee"
     :trigger       [:span.quick-add-assignee-chip
                     [person-icon]
                     (or (data/assignee-name assignee)
                         (data/contact-name assignee)
                         "Unassigned")]}]])

(defn- add-section-pills
  "Trigger pills for the sections that are not open yet — Add description, the
   deadline picker, Add links. `open?` is a predicate on the section key;
   `on-add` receives the section key to open. `separated?` draws a rule above
   the row to fence it off from open sections."
  [{:keys [open? on-add separated?]}]
  (let [closed? (complement open?)]
    (when (some closed? [:description :deadline :links])
      [:div.quick-add-pills {:class (when separated? "separated")}
       (when (closed? :description)
         [:button.quick-add-pill
          {:type "button" :on-click #(on-add :description)}
          [desc-icon] "Add description"])
       (when (closed? :deadline)
         [:button.quick-add-pill
          {:type "button" :on-click #(on-add :deadline)}
          [deadline-icon] "Add deadline"])
       (when (closed? :links)
         [:button.quick-add-pill
          {:type "button" :on-click #(on-add :links)}
          [link-icon] "Add links"])])))

(defn- form-footer
  [{:keys [can-save? hint save-label on-cancel on-save]}]
  [:div.quick-add-footer
   (when hint
     [:span.quick-add-hint
      {:style (when-not can-save? {:visibility "hidden"})}
      hint])
   [:button.quick-add-cancel
    {:type "button" :on-click on-cancel}
    "Cancel"]
   [:button.quick-add-save
    {:type "button" :disabled (not can-save?) :on-click on-save}
    save-label]])

(defn- task-form-render
  "Title field plus the optional description / deadline / links sections, each
   shown in place of its Add pill. `:collapsed-until-typed?` hides everything
   past the title until the title has text.

   The assignee is deliberately not a form field: it lives behind its own
   `task/assign` mutation rather than in `task/update`'s params, so it's staged
   here and handed to `on-save` for the caller to apply."
  [{:form/keys [fields]}
   {:keys [placeholder primary-label save-label show-hint? on-save on-cancel
           auto-focus? collapsed-until-typed? assignable? initial-assignee]}]
  (r/with-let [!open-sections (r/atom (initial-open-sections fields))
               !pending-desc-focus? (atom false)
               !pending-calendar? (atom false)
               !links-adder-open? (r/atom false)
               !assignee (r/atom initial-assignee)]
    (let [{:keys [title-val desc-val urgency-val deadline-val link-urls deadline-field
                  set-title! set-desc! set-urgency! set-deadline! set-links!]} (read-fields fields)
          revealed? (or (not collapsed-until-typed?)
                        (not (str/blank? title-val)))
          can-save? (not (str/blank? title-val))
          missing? (and (seq desc-val) (not can-save?))
          open-sections @!open-sections
          links-adder-open? @!links-adder-open?
          links-shown? (or links-adder-open? (seq link-urls))
          visible-sections (if links-shown?
                             open-sections
                             (vec (remove #{:links} open-sections)))
          open? (set visible-sections)
          submit (fn []
                   (when can-save?
                     (on-save {:title (str/trim title-val)
                               :description desc-val
                               :urgency urgency-val
                               :deadline deadline-val
                               :urls link-urls
                               :assignee @!assignee})))
          open-section! (fn [section]
                          (case section
                            :description (reset! !pending-desc-focus? true)
                            :deadline (reset! !pending-calendar? true)
                            :links (reset! !links-adder-open? true))
                          (swap! !open-sections
                                 #(if (some #{section} %) % (conj % section))))
          close-section! (fn [section]
                           (swap! !open-sections #(vec (remove #{section} %))))
          section-view (fn [section]
                         (case section
                           :description
                           [description-section
                            {:value desc-val
                             :input-ref (fn [el]
                                          (when (and el @!pending-desc-focus?)
                                            (reset! !pending-desc-focus? false)
                                            (.focus el)))
                             :on-change set-desc!
                             :on-submit submit
                             :on-cancel on-cancel
                             :on-close #(close-section! :description)}]
                           :deadline
                           [deadline-section
                            {:field deadline-field
                             :pending-calendar? !pending-calendar?
                             :on-remove (fn []
                                          (set-deadline! nil)
                                          (close-section! :deadline))}]
                           :links
                           [links-section-view
                            {:urls link-urls
                             :adder-open? links-adder-open?
                             :on-add (fn [url]
                                       (when-not (some #{url} link-urls)
                                         (set-links! (conj link-urls url))))
                             :on-remove-link (fn [url]
                                               (set-links! (vec (remove #{url} link-urls))))
                             :on-open-adder #(reset! !links-adder-open? true)
                             ;; Just collapse the adder; the links and the section
                             ;; stay. link-adder freezes this handler at mount, so
                             ;; it must not read the live link count.
                             :on-cancel-adder #(reset! !links-adder-open? false)}]))]
      [:<>
       [title-input
        {:value title-val
         :placeholder placeholder
         :missing? missing?
         :auto-focus? auto-focus?
         :on-change set-title!
         :on-submit submit
         :on-cancel on-cancel}]
       (when revealed?
         [:<>
          ;; TODO(evgheni): promote section-view to a top-level component taking
          ;; its deps as props, then ^{:key section} [section-view ...] works
          ;; directly. As an inline closure it remounts each render, so it must
          ;; be called and keyed via with-meta on the returned vector.
          (for [section visible-sections]
            (with-meta (section-view section) {:key section}))
          [view/urgency-control {:value urgency-val :on-change set-urgency!}]
          ;; Always shown rather than pill-gated like the sections above: a task
          ;; is always assigned to someone, so there's nothing to "add".
          (when assignable?
            [assignee-section {:assignee @!assignee
                               :on-pick #(reset! !assignee %)}])
          [add-section-pills
           {:open? open?
            :on-add open-section!
            :separated? (boolean (seq visible-sections))}]
          [form-footer
           {:can-save? can-save?
            :hint (when show-hint?
                    (submit-hint {:multiline? (open? :description) :action primary-label}))
            :save-label save-label
            :on-cancel on-cancel
            :on-save submit}]])])))

(defn task-form
  "Title + urgency + description + deadline + links form, shared by the desktop
   and mobile Add composer and row edit. Wrap it in [[quick-add]]. `:form-id`
   scopes the form, `:initial-value` seeds it, `:on-save` receives
   `{:title :description :urgency :deadline :urls :assignee}`. `:save-label` is
   the Save button text; `:show-hint?` toggles the keyboard hint (off for touch).

   `:assignable?` shows the assignee picker, seeded with `:initial-assignee`
   (the task's `:task/assigned-to`). `:assignee` comes back as the picked
   contact — apply it with [[hai.tasks.data/assign]], which is a separate
   mutation from the `task/update` the other fields go through. Without
   `:assignable?` the key is absent and the form behaves exactly as before."
  [{:keys [form-id initial-value placeholder primary-label save-label show-hint?
           on-save on-cancel auto-focus? collapsed-until-typed?
           assignable? initial-assignee]
    :or {save-label "Save" show-hint? true}}]
  [form/component
   {:id form-id
    :schema data/task-form-schema
    :initial-value (or initial-value {:task/title "" :task/urgency :task.urgency/later})
    :render (fn [form]
              ^{:key (:form/setup-id form)}
              [task-form-render form
               {:placeholder placeholder
                :primary-label primary-label
                :save-label save-label
                :show-hint? show-hint?
                :on-save on-save
                :on-cancel on-cancel
                :auto-focus? auto-focus?
                :collapsed-until-typed? collapsed-until-typed?
                :assignable? assignable?
                :initial-assignee initial-assignee}])}])
